import React, { useState, useEffect } from 'react';
import { message, Drawer, Button } from 'antd';
import type { TableProps } from 'antd';
import { useSearchParams } from 'react-router-dom';
import GenericTable from '../../components/common/Table';
import GenericSearch from '../../components/common/Search';
import PropertySaleDetailsContent from '../../components/PropertySaleDetailsContent';
import SimulationReasonModal from './SimulationReasonModal';
import { ExportColumn, SearchFilters } from '../../types/TableTypes';
import { IPropertySale, IPropertySimulation } from 'types/PropertyTypes';
import * as propertySaleHistoryApi from '../../services/propertySaleHistoryApi';
import * as simulationApi from '../../services/simulationApi';
import { calcBuildingAge } from '../../utils/simulationCalc';
import { buildDrivePreviewUrl } from '../../utils/driveUrl';
const formatJapaneseDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "N/A";

  let date: Date;

  // Kiểm tra nếu là format YYYYMMDD (8 ký tự số)
  if (dateString.length === 8 && /^\d{8}$/.test(dateString)) {
    const year = parseInt(dateString.substring(0, 4));
    const month = parseInt(dateString.substring(4, 6)) - 1; // Month is 0-indexed
    const day = parseInt(dateString.substring(6, 8));
    date = new Date(year, month, day);
  } else {
    // Thử parse như date string bình thường
    date = new Date(dateString);
  }

  if (isNaN(date.getTime())) return "N/A";

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  return `${year}年${month}月${day}日`;
};



// Function helper để tạo date render
const createDateRender = (dateField: keyof IPropertySale) => {
  return (_: any, record: IPropertySale) => formatJapaneseDate(record[dateField] as string);
};

const getPropertyColumns = (
  onAddSimulation: (e: React.MouseEvent, record: IPropertySale) => void,
  addingSet: Set<number>,
  simulatedIds: Set<number>,
): TableProps<IPropertySale>["columns"] => [
  {
    title: "",
    key: "simulation",
    width: 80,
    render: (_: any, record: IPropertySale) => {
      const isSimulated = simulatedIds.has(record.id);
      return (
        <Button
          size="small"
          disabled={isSimulated}
          loading={addingSet.has(record.id)}
          onClick={(e) => onAddSimulation(e, record)}
          style={
            isSimulated
              ? { background: '#E5E7EB', border: 'none', color: '#9CA3AF', fontWeight: 500, cursor: 'not-allowed' }
              : { background: '#38BDF8', border: 'none', color: 'white', fontWeight: 500 }
          }
        >
          査定
        </Button>
      );
    },
  },
  {
    title: "取込日時",
    dataIndex: "updated_at",
    key: "updated_at",
    render: createDateRender("updated_at"),
    sorter: true,
  },
  {
    title: "物件名",
    dataIndex: "building_name",
    key: "building_name",
    sorter: true,
    width: 230,
  },
  {
    title: "物件番号",
    dataIndex: "property_number",
    key: "property_number",
  },
  {
    title: "取引態様",
    dataIndex: "transaction_type",
    key: "transaction_type",
  },
  {
    title: "住所",
    dataIndex: "address_combined",
    key: "address_combined",
    render: (_, record) => {
      const address1 = record.address_1 || "";
      const address2 = record.address_2 || "";
      const address3 = record.address_3 || "";
      const combined = [address1, address2, address3].filter(val => val.trim() !== "").join("<br />");
      if (!combined) return "N/A";
      return <span dangerouslySetInnerHTML={{ __html: combined }} />;
    },
    sorter: false,
  },
  {
    title: <span dangerouslySetInnerHTML={{ __html: "最寄駅<br />駅徒歩" }} />,
    dataIndex: "nearest_station",
    key: "nearest_station",
    render: (_, record) => {
      const line = record.line_1 || "";
      const station = record.station_1 ? record.station_1 + "駅" : "";
      const lineAndStation = (line && station)
        ? `${line}<br />${station}`
        : (line + station);
      const walk = record.walk_minutes_1 ? `徒歩${record.walk_minutes_1}分` : "";
      const combined = [lineAndStation, walk].filter(val => val.trim() !== "").join("<br />");
      if (!combined) return "N/A";
      return <span dangerouslySetInnerHTML={{ __html: combined }} />;
    },
  },
  {
    title: "価格",
    dataIndex: "price_total",
    key: "price_total",
    width: 80,
    render: (_, record) => {
      const formatNumber = (num: number | string) => {
        if (num === undefined || num === null || num === "") return "-";
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      };
      return record.price_total ? `${formatNumber(record.price_total)}万` : "-";
    },
    sorter: true,
  },
  {
    title: "坪単価",
    dataIndex: "price_per_tsubo",
    key: "price_per_tsubo",
    width: 80,
    render: (_, record) => {
      const formatNumber = (num: number | string) => {
        if (num === undefined || num === null || num === "") return "-";
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      };
      return record.price_per_tsubo ? `${formatNumber(record.price_per_tsubo)}万` : "-";
    },
    sorter: true,
  },
  {
    title: <span dangerouslySetInnerHTML={{ __html: "占有面積<br />坪数 <br />間取り" }} />,
    dataIndex: "area_m2",
    key: "area_m2",
    render: (_, record) => {
      let area_m2 = record.area_m2 !== undefined && record.area_m2 !== null && record.area_m2 !== '' ? `${record.area_m2}㎡` : "-";
      let area_tubo = record.area_tubo !== undefined && record.area_tubo !== null && record.area_tubo !== '' ? `${record.area_tubo}坪` : "-";
      const layout_type = record.layout_type || "-";

      // すべて「-」なら「-」のみ表示
      if (area_m2 === "-" && area_tubo === "-" && layout_type === "-") {
        return <span>-</span>;
      }
      const combinedValues = [area_m2, area_tubo, layout_type].join("<br />");
      return <span dangerouslySetInnerHTML={{ __html: combinedValues }} />;
    },
    sorter: true,
  },
  {
    title: <span dangerouslySetInnerHTML={{ __html: "築年数" }} />,
    dataIndex: "built_year",
    key: "built_year",
    render: (_, record) => {
      const age = calcBuildingAge(record.built_year?.toString());
      return age !== null ? `${age}年` : "N/A";
    },
    sorter: true,
  },
  {
    title: "図面リンク",
    dataIndex: "floor_plan_path",
    key: "floor_plan_path",
    render: (_, record) => {
      const driveUrl = buildDrivePreviewUrl(record.floor_plan_path);
      if (driveUrl) {
        return (
          <a href={driveUrl} target="_blank" rel="noopener noreferrer">
            図面
          </a>
        );
      }
      return "N/A";
    },
    sorter: true,
  },

  {
    title: "登録日",
    dataIndex: "registered_at",
    key: "registered_at",
    width: 120,
    render: createDateRender("registered_at"),
    sorter: true,
  },
];

const propertyExportColumns: ExportColumn[] = [
  { key: "id", title: "ID", dataIndex: "id" },
  { key: "property_type", title: "物件種目", dataIndex: "property_type" },
  { key: "building_name", title: "物件名", dataIndex: "building_name" },
  { key: "room_number", title: "部屋番号", dataIndex: "room_number" },
  { key: "status", title: "ステータス", dataIndex: "status" },



  { key: "property_number", title: "物件番号", dataIndex: "property_number" },
  { key: "transaction_type", title: "取引態様", dataIndex: "transaction_type" },


  { key: "site_url", title: "物件詳細ページ", dataIndex: "site_url" },
  { key: "registered_at", title: "掲載開始日", dataIndex: "registered_at" },
  { key: "closed_at", title: "掲載終了日", dataIndex: "closed_at" },
  { key: "post_date", title: "掲載日時", dataIndex: "post_date" },
  { key: "updated_at_site", title: "情報更新日", dataIndex: "updated_at_site" },
  { key: "contracted_at", title: "成約年月日", dataIndex: "contracted_at" },

  { key: "price_total", title: "価格", dataIndex: "price_total" },
  { key: "yield", title: "利回り", dataIndex: "yield" },
  { key: "price_per_m2", title: "㎡単価", dataIndex: "price_per_m2" },
  { key: "price_per_tsubo", title: "坪単価", dataIndex: "price_per_tsubo" },
  { key: "area_m2", title: "専有面積(㎡)", dataIndex: "area_m2" },
  { key: "area_tubo", title: "坪数", dataIndex: "area_tubo" },
  { key: "balcony_area_m2", title: "バルコニー面積", dataIndex: "balcony_area_m2" },
  { key: "prefecture", title: "都道府県", dataIndex: "prefecture" },
  { key: "address_1", title: "市区名", dataIndex: "address_1" },
  { key: "address_2", title: "町名", dataIndex: "address_2" },
  { key: "address_3", title: "番地など", dataIndex: "address_3" },
  { key: "line_1", title: "沿線名1", dataIndex: "line_1" },
  { key: "station_1", title: "駅名1", dataIndex: "station_1" },
  { key: "walk_minutes_1", title: "駅徒歩1(分)", dataIndex: "walk_minutes_1" },
  { key: "line_2", title: "沿線名2", dataIndex: "line_2" },
  { key: "station_2", title: "駅名2", dataIndex: "station_2" },
  { key: "walk_minutes_2", title: "駅徒歩2(分)", dataIndex: "walk_minutes_2" },
  { key: "line_3", title: "沿線名3", dataIndex: "line_3" },
  { key: "station_3", title: "駅名3", dataIndex: "station_3" },
  { key: "walk_minutes_3", title: "駅徒歩3(分)", dataIndex: "walk_minutes_3" },
  { key: "layout_type", title: "間取りタイプ", dataIndex: "layout_type" },
  // { key: "layout_room_count", title: "部屋数", dataIndex: "layout_room_count" },
  { key: "built_year", title: "築年", dataIndex: "built_year" },
  { key: "structure", title: "建物構造", dataIndex: "structure" },
  { key: "floor_count", title: "地上階数", dataIndex: "floor_count" },
  { key: "balcony_direction", title: "バルコニー向き", dataIndex: "balcony_direction" },
  { key: "has_management_union", title: "管理組合の有無", dataIndex: "has_management_union" },
  { key: "management_fee", title: "管理費", dataIndex: "management_fee" },
  { key: "reserve_fund", title: "修繕積立金", dataIndex: "reserve_fund" },
  { key: "management_type", title: "管理形態", dataIndex: "management_type" },
  { key: "management_company", title: "管理会社名", dataIndex: "management_company" },
  { key: "construction_company", title: "施工会社名", dataIndex: "construction_company" },
  { key: "developer", title: "分譲会社名", dataIndex: "developer" },
  { key: "has_parking", title: "駐車場有無", dataIndex: "has_parking" },
  { key: "parking_fee", title: "駐車場月額", dataIndex: "parking_fee" },
  { key: "property_status", title: "現況", dataIndex: "property_status" },
  { key: "land_ownership", title: "土地権利", dataIndex: "land_ownership" },
  { key: "note_1", title: "備考1", dataIndex: "note_1" },
  { key: "note_2", title: "備考2", dataIndex: "note_2" },
  { key: "note_3", title: "備考3", dataIndex: "note_3" },
  { key: "note_4", title: "備考4", dataIndex: "note_4" },
  { key: "floor_plan_path", title: "物件図面ファイルパス", dataIndex: "floor_plan_path" },
  { key: "created_at", title: "登録日時", dataIndex: "created_at" },
  { key: "updated_at", title: "更新日時", dataIndex: "updated_at" },
];

const PropertySaleHistories: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [tableData, setTableData] = useState<IPropertySale[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState<number>(0);
  const [addingSimulation, setAddingSimulation] = useState<Set<number>>(new Set());
  const [simulatedIds, setSimulatedIds] = useState<Set<number>>(new Set());
  const [simulationMap, setSimulationMap] = useState<Map<number, IPropertySimulation>>(new Map());
  
  // Modal state for simulation reason
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reasonModalTarget, setReasonModalTarget] = useState<IPropertySale | null>(null);
  const [reasonModalLoading, setReasonModalLoading] = useState(false);

  // Drawer state for property details
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<IPropertySale | undefined>(undefined);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // URLパラメータから状態を取得
  const currentPage = parseInt(searchParams.get('page') || '1');
  const pageSize = parseInt(searchParams.get('pageSize') || '50');
  const sortField = searchParams.get('sortField') || '';
  const sortOrder = searchParams.get('sortOrder') as 'ascend' | 'descend' | undefined;
  const keyword = searchParams.get('keyword') || '';
  const station = searchParams.get('station') || '';
  const district = searchParams.get('district') || '';
  const priceMinParam = searchParams.get('priceMin');
  const priceMaxParam = searchParams.get('priceMax');
  const [currentFilters, setCurrentFilters] = useState<SearchFilters>({
    keyword,
    ...(station ? { station } : {}),
    ...(district ? { district: district.split(',') } : {}),
    ...(priceMinParam ? { priceMin: Number(priceMinParam) } : {}),
    ...(priceMaxParam ? { priceMax: Number(priceMaxParam) } : {}),
  });
  const [districtOptions, setDistrictOptions] = useState<{ label: string; value: string }[]>([]);
  const [propertyType, setPropertyType] = useState<'all' | 'sale' | 'contracted'>(
    searchParams.get('propertyType') as 'all' | 'sale' | 'contracted' || 'all'
  );

  const buildApiParams = (
    page: number,
    size: number | 'all',
    filters: SearchFilters,
    sortField: string,
    sortOrder: 'ascend' | 'descend' | undefined,
    propType: string,
  ): propertySaleHistoryApi.PropertySaleHistoryParams => {
    const params: propertySaleHistoryApi.PropertySaleHistoryParams = {
      page: size === 'all' ? undefined : page,
      per_page: size,
    };
    if (propType !== 'all') params.property_type = propType;
    if (filters.keyword) params.search = filters.keyword;
    if (filters.station) params.station = filters.station;
    if (filters.district && Array.isArray(filters.district) && filters.district.length > 0) {
      params.district = filters.district.join(',');
    }
    if (filters.priceMin != null) params.priceMin = filters.priceMin;
    if (filters.priceMax != null) params.priceMax = filters.priceMax;
    if (filters.saleDateStart) params.saleDateStart = filters.saleDateStart.format('YYYY-MM-DD HH:mm:ss');
    if (filters.saleDateEnd) params.saleDateEnd = filters.saleDateEnd.format('YYYY-MM-DD HH:mm:ss');
    if (filters.contractedDateStart) params.contractedDateStart = filters.contractedDateStart.format('YYYY-MM-DD HH:mm:ss');
    if (filters.contractedDateEnd) params.contractedDateEnd = filters.contractedDateEnd.format('YYYY-MM-DD HH:mm:ss');
    if (sortField && sortOrder) {
      params.sort_field = sortField;
      params.sort_order = sortOrder;
    }
    return params;
  };

  const loadData = async (
    page: number = 1,
    size: number = 10,
    filters: SearchFilters = {},
    sortField: string = '',
    sortOrder: 'ascend' | 'descend' | undefined = undefined,
    propType: 'all' | 'sale' | 'contracted' = 'all'
  ) => {
    try {
      setLoading(true);

      const params = buildApiParams(page, size, filters, sortField, sortOrder, propType);
      const results = await propertySaleHistoryApi.getList(params);
      const data = results.data?.map((element: IPropertySale) => ({ ...element, key: String(element.id) })) || [];

      setTableData(data);
      setTotal(results.total || results.meta?.total || 0);

      // URLパラメータを更新
      const newParams = new URLSearchParams();
      newParams.set('page', page.toString());
      newParams.set('pageSize', size.toString());
      if (filters.keyword) newParams.set('keyword', filters.keyword);
      if (filters.station) newParams.set('station', filters.station);
      if (filters.district && Array.isArray(filters.district) && filters.district.length > 0) {
        newParams.set('district', filters.district.join(','));
      }
      if (filters.priceMin != null) newParams.set('priceMin', String(filters.priceMin));
      if (filters.priceMax != null) newParams.set('priceMax', String(filters.priceMax));
      if (filters.saleDateStart) newParams.set('saleDateStart', filters.saleDateStart.format('YYYY-MM-DD HH:mm:ss'));
      if (filters.saleDateEnd) newParams.set('saleDateEnd', filters.saleDateEnd.format('YYYY-MM-DD HH:mm:ss'));
      if (filters.contractedDateStart) newParams.set('contractedDateStart', filters.contractedDateStart.format('YYYY-MM-DD HH:mm:ss'));
      if (filters.contractedDateEnd) newParams.set('contractedDateEnd', filters.contractedDateEnd.format('YYYY-MM-DD HH:mm:ss'));
      if (sortField) newParams.set('sortField', sortField);
      if (sortOrder) newParams.set('sortOrder', sortOrder);
      if (propType !== 'all') newParams.set('propertyType', propType);
      setSearchParams(newParams, { replace: true });
    } catch (error) {
      console.error("Failed to fetch data:", error);
      message.error('データの読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const loadSimulatedIds = async () => {
    try {
      const data = await simulationApi.getAllIds();
      setSimulatedIds(new Set(data.ids));
      const map = new Map<number, IPropertySimulation>();
      for (const [key, val] of Object.entries(data.simulations)) {
        map.set(Number(key), val as unknown as IPropertySimulation);
      }
      setSimulationMap(map);
    } catch {
      // 取得失敗時はグレーアウトなしで続行
    }
  };

  const loadDistrictOptions = async () => {
    try {
      const result = await propertySaleHistoryApi.getDistricts();
      setDistrictOptions(result.districts.map(d => ({ label: d, value: d })));
    } catch {
      // 取得失敗時は空のまま続行
    }
  };

  const loadInitialData = async () => {
    // URLパラメータから初期値を読み込む
    const filters: SearchFilters = {};
    if (keyword) filters.keyword = keyword;
    if (station) filters.station = station;
    if (district) filters.district = district.split(',');
    await Promise.all([
      loadData(currentPage, pageSize, filters, sortField, sortOrder, propertyType),
      loadSimulatedIds(),
      loadDistrictOptions(),
    ]);
    
    // スクロール位置を復元
    const savedScrollTop = sessionStorage.getItem('tableScrollTop');
    if (savedScrollTop) {
      setTimeout(() => {
        const tableBody = document.querySelector('.custom-table .ant-table-body');
        if (tableBody) {
          (tableBody as HTMLElement).scrollTop = parseInt(savedScrollTop);
        }
        sessionStorage.removeItem('tableScrollTop');
      }, 100);
    }
  };

  const handleSearch = async (filters: SearchFilters) => {
    setCurrentFilters(filters);
    await loadData(1, pageSize, filters, sortField, sortOrder, propertyType);
    message.success('検索しました');
  };

  const handleResetSearch = async () => {
    setCurrentFilters({});
    await loadData(1, pageSize, {}, '', undefined, propertyType);
    message.info('フィルターをクリアしました');
  };

  const handlePageChange = async (page: number, size: number) => {
    await loadData(page, size, currentFilters, sortField, sortOrder, propertyType);
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const { field, order } = sorter;
    
    // Reload data with new sort parameters
    loadData(currentPage, pageSize, currentFilters, field || '', order, propertyType);
  };

  const handlePropertyTypeChange = async (type: 'all' | 'sale' | 'contracted') => {
    setPropertyType(type);
    await loadData(1, pageSize, currentFilters, sortField, sortOrder, type);
    message.success('物件種別を変更しました');
  };

  const fetchPropertyDetails = async (propertyNumber: string) => {
    try {
      setDetailLoading(true);
      const data = await propertySaleHistoryApi.getByPropertyNumber(propertyNumber);
      setSelectedProperty(data);
    } catch (error) {
      console.error("Failed to fetch property details:", error);
      message.error('詳細情報の読み込みに失敗しました');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDetailClick = (propertyNumber: string) => {
    setDrawerVisible(true);
    fetchPropertyDetails(propertyNumber);
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setSelectedProperty(undefined);
  };

  const handleReasonsUpdate = async (simId: number, goodReason: string, badReason: string) => {
    const updated = await simulationApi.update(simId, { good_reason: goodReason, bad_reason: badReason } as Partial<IPropertySimulation>);
    setSimulationMap(prev => {
      const next = new Map(prev);
      next.set(updated.property_sale_history_id, updated);
      return next;
    });
  };

  const handleAddSimulation = (e: React.MouseEvent, record: IPropertySale) => {
    e.stopPropagation();
    setReasonModalTarget(record);
    setReasonModalOpen(true);
  };

  const handleReasonSubmit = async (goodReason: string, badReason: string) => {
    if (!reasonModalTarget) return;
    const record = reasonModalTarget;
    setReasonModalLoading(true);
    setAddingSimulation(prev => { const next = new Set(prev); next.add(record.id); return next; });
    try {
      const result = await simulationApi.create(record.id, goodReason, badReason);
      if (result === null) {
        message.info('既にシミュレーション済みです');
        return;
      }
      setSimulatedIds(prev => { const next = new Set(prev); next.add(record.id); return next; });
      setSimulationMap(prev => { const next = new Map(prev); next.set(record.id, result); return next; });
      message.success('シミュレーションに追加しました');
      setReasonModalOpen(false);
    } catch {
      message.error('追加に失敗しました');
    } finally {
      setReasonModalLoading(false);
      setAddingSimulation(prev => {
        const next = new Set(prev);
        next.delete(record.id);
        return next;
      });
    }
  };

  const handleReasonCancel = () => {
    setReasonModalOpen(false);
    setReasonModalTarget(null);
  };

  /**
   * CSV全件エクスポート用: 現在のフィルター条件で全データを取得する
   */
  const fetchAllDataForExport = async (): Promise<IPropertySale[]> => {
    const params = buildApiParams(1, 'all', currentFilters, sortField, sortOrder, propertyType);
    const results = await propertySaleHistoryApi.getList(params);
    return results.data?.map((element: IPropertySale) => ({ ...element, key: String(element.id) })) || [];
  };

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="dark-theme-container">

      <GenericSearch
        onSearch={handleSearch}
        onReset={handleResetSearch}
        loading={loading}
        keywordPlaceholder="物件名、物件番号、住所で検索..."
        stationPlaceholder="駅名で検索（, 区切りで複数指定）"
        districtOptions={districtOptions}
        districtPlaceholder="区を選択"
        showPriceFilter
      />

      <GenericTable<IPropertySale>
        data={tableData}
        allData={tableData}
        columns={getPropertyColumns(handleAddSimulation, addingSimulation, simulatedIds)}
        exportColumns={propertyExportColumns}
        total={total}
        pageSize={pageSize}
        currentPage={currentPage}
        loading={loading}
        onPageChange={handlePageChange}
        onChange={handleTableChange}
        onRow={(record) => ({
          onClick: () => {
            handleDetailClick(record.property_number || "");
          },
          style: Object.assign(
            { cursor: 'pointer' },
            record.status && record.status !== 'open'
              ? { background: '#F3F4F6', color: '#9CA3AF', opacity: 0.7 }
              : {}
          ),
        })}
        tableTitle=''
        exportFilename='データ'
        propertyType={propertyType}
        onPropertyTypeChange={handlePropertyTypeChange}
        fetchAllData={fetchAllDataForExport}
      />

      <SimulationReasonModal
        open={reasonModalOpen}
        loading={reasonModalLoading}
        onSubmit={handleReasonSubmit}
        onCancel={handleReasonCancel}
      />

      <Drawer
        title="物件詳細"
        placement="right"
        onClose={handleDrawerClose}
        open={drawerVisible}
        width="44%"
        styles={{
          header: {
            background: '#FFFFFF',
            borderBottom: '1px solid #E5E7EB',
          },
          body: {
            background: '#FFFFFF',
            paddingTop: 24,
          },
        }}
      >
        <PropertySaleDetailsContent
          propertySale={selectedProperty}
          loading={detailLoading}
          goodReason={selectedProperty ? simulationMap.get(selectedProperty.id)?.good_reason : undefined}
          badReason={selectedProperty ? simulationMap.get(selectedProperty.id)?.bad_reason : undefined}
          simulationId={selectedProperty ? simulationMap.get(selectedProperty.id)?.id : undefined}
          onReasonsUpdate={handleReasonsUpdate}
        />
      </Drawer>
    </div>
  );
};

export default PropertySaleHistories;